import { customDisciplineLabel } from "../../../util/src/custom-disciplines/labels.js";
import { useCompiledLayoutRuntimeData } from "./CompiledLayoutRuntimeContext.js";
import { Button } from "@mui/material";
import React from "react";
import type { RenderCustomDisciplineButtonNode as RenderCustomDisciplineButtonNodeModel } from "../../../util/src/layout-compiler/types.js";
import { renderMuiIcon } from "../components/MuiIconResolver.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { RenderNodeTooltipShell } from "./RenderNodeTooltipShell.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderCustomDisciplineButtonNodeProps = {
  node: RenderCustomDisciplineButtonNodeModel;
  scale: number;
};

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

/**
 * Renders a `customDisciplineButton` component node (custom discipline cell/modal
 * layouts). Clicking the button dispatches a node-targeted `onClick` behavior
 * re-evaluation; any resulting `customDiscipline.action` effect is surfaced to
 * the host page via `CompiledLayoutRenderer`'s `onCustomDisciplineAction` prop.
 */
export function RenderCustomDisciplineButtonNode({
  node,
  scale,
}: RenderCustomDisciplineButtonNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const labels = useCompiledLayoutRuntimeData()?.__customDisciplineLabels;
  const clipStyle = getRenderNodeClipStyle(node, scale);

  return (
    <RenderNodeTooltipShell
      tooltip={node.tooltip}
      ownerNodeKey={node.key ?? node.id}
    >
      <Button
        {...runtimeProps}
        variant={node.props.variant ?? "contained"}
        size={node.props.size ?? "small"}
        color={node.props.color ?? "primary"}
        disabled={node.props.disabled === true}
        autoFocus={node.props.autoFocus === true}
        startIcon={renderMuiIcon(node.props.startIcon)}
        sx={{
          ...(node.style.position === "relative"
            ? {
                position: "relative",
                flexShrink: 0,
              }
            : {
                position: "absolute",
                left: node.x * scale,
                top: node.y * scale,
              }),
          width: node.width * scale,
          height: node.height * scale,
          zIndex: node.zIndex,
          opacity: node.style.opacity as number | undefined,
          boxSizing: "border-box",
          minWidth: 0,
          justifyContent:
            node.style.textAlign === "left"
              ? "flex-start"
              : node.style.textAlign === "right"
                ? "flex-end"
                : "center",
          ...(node.style as React.CSSProperties),
          transform: composeTransform(node.rotation, node.style?.transform),
          transformOrigin: "center center",
          ...clipStyle,
        }}
      >
        {customDisciplineLabel(labels, node.props.label ?? "")}
      </Button>
    </RenderNodeTooltipShell>
  );
}
